import React, { useState } from 'react';
import { CameraVideoFill, FileEarmarkCode, Images } from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { useError } from '../../contexts/ErrorContext';
import { RecordsAPI, VideosAPI } from '../../services/api';
import { Video, VideoRecord } from '../../types';
import DownloadComponent from '../DownloadComponent';

interface DownloadForSelectedRecordsProps {
    records: VideoRecord[];
}

const DownloadForSelectedRecords: React.FC<DownloadForSelectedRecordsProps> = ({ records }) => {
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showL2rModal, setShowL2rModal] = useState(false);
    const [showExtracteddModal, setShowExtractedModal] = useState(false);
    const [showProcessedModal, setShowProcessedModal] = useState(false);
    const [videoUrls, setVideoUrls] = useState<string[]>([]);
    const [l2rUrls, setL2rUrls] = useState<string[]>([]);
    const [extractedUrls, setExtractedUrls] = useState<string[]>([]);
    const [processedUrls, setProcessedUrls] = useState<string[]>([]);
    const [isGeneratingUrls, setIsGeneratingUrls] = useState(false);
    const [totalVideoSize, setTotalVideoSize] = useState(0);
    const { setError } = useError();
    const l2r_identifier = "_l2r_";
    const frameFolderName = "frames"
    const equirectFolderName = "equirects"

    // Helper function to format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleShowVideoModal = async () => {
        setShowVideoModal(true);
        setIsGeneratingUrls(true);

        try {
            // First refresh all video URLs
            const refreshPromises = [];
            for (const record of records) {
                for (const video of record.videos) {
                    refreshPromises.push(VideosAPI.refreshUrl(video.id));
                }
            }

            // Wait for all refresh operations to complete
            const refreshResult = await Promise.all(refreshPromises);
            const urls: string[] = [];
            let totalSize = 0;

            refreshResult.forEach(videoData => {
                const video: Video = videoData.data;
                if (video.url) {
                    urls.push(video.url);
                    totalSize += video.size;
                }
            });

            setVideoUrls(urls);
            setTotalVideoSize(totalSize);
        } catch (error) {
            setError(`Error generating video URLs: ${error}`);
        } finally {
            setIsGeneratingUrls(false);
        }
    };

    const handleShowL2rModal = async () => {
        setShowL2rModal(true);
        setIsGeneratingUrls(true);

        try {
            // Get all l2r_result.json files from all records
            const l2rUrlsPromises = records.map(record =>
                RecordsAPI.listBlobDownloadUrlsWithFolders(record.id, 'extracted')
                    .then(response => {
                        const rootUrls: string[] | undefined = response.data.folders["root"];
                        if (!rootUrls) {
                            return [];
                        }
                        const l2rUrls = rootUrls.filter(url => {
                            const urlWithoutParams = url.split('?')[0];
                            return urlWithoutParams.includes(l2r_identifier);
                        });
                        return l2rUrls;
                    })
            );

            // Wait for all API calls to complete and combine the results
            const urlArrays = await Promise.all(l2rUrlsPromises);
            const combinedUrls = urlArrays.flat();

            setL2rUrls(combinedUrls);
        } catch (error) {
            setError(`Error generating L2R result URLs: ${error}`);
        } finally {
            setIsGeneratingUrls(false);
        }
    };

    const handleShowExtracteddModal = async () => {
        setShowExtractedModal(true);
        setIsGeneratingUrls(true);

        try {
            // Get all processed images files from all records
            // potentially much too much to handle. To be checked
            const extractedUrlsPromises = records.map(record =>
                RecordsAPI.listBlobDownloadUrlsWithFolders(record.id, 'extracted')
                    .then(response => {
                        let processedUrls: string[] | undefined = response.data.folders[equirectFolderName];
                        if (!processedUrls) {
                            processedUrls = response.data.folders[frameFolderName];
                        }
                        if (!processedUrls) {
                            return [];
                        }
                        return processedUrls;
                    })
            );

            // Wait for all API calls to complete and combine the results
            const urlArrays = await Promise.all(extractedUrlsPromises);
            const combinedUrls = urlArrays.flat();
            setExtractedUrls(combinedUrls);
        } catch (error) {
            setError(`Error generating Extracted result URLs: ${error}`);
        } finally {
            setIsGeneratingUrls(false);
        }
    };

    const handleShowProcessedModal = async () => {
        setShowProcessedModal(true);
        setIsGeneratingUrls(true);

        try {
            // Get all processed images files from all records
            // potentially much too much to handle. To be checked
            const processedUrlsPromises = records.map(record =>
                RecordsAPI.listBlobDownloadUrlsWithFolders(record.id, 'processed')
                    .then(response => {
                        let processedUrls: string[] | undefined = response.data.folders[equirectFolderName];
                        if (!processedUrls) {
                            processedUrls = response.data.folders[frameFolderName];
                        }
                        if (!processedUrls) {
                            return [];
                        }
                        return processedUrls;
                    })
            );

            // Wait for all API calls to complete and combine the results
            const urlArrays = await Promise.all(processedUrlsPromises);
            const combinedUrls = urlArrays.flat();
            setProcessedUrls(combinedUrls);
        } catch (error) {
            setError(`Error generating Procesed result URLs: ${error}`);
        } finally {
            setIsGeneratingUrls(false);
        }
    };

    const handleCloseVideoModal = () => {
        setShowVideoModal(false);
    };

    const handleCloseL2rModal = () => {
        setShowL2rModal(false);
    };

    const handleCloseExtractedModal = () => {
        setShowExtractedModal(false);
    };

    const handleCloseProcessedModal = () => {
        setShowProcessedModal(false);
    };

    const getTotalVideoCount = (): number => {
        return records.reduce((count, record) => count + record.videos.length, 0);
    };

    return (
        <div className="download-for-selected-records mb-3">
            <div className="d-flex gap-2">
                <Button
                    variant="primary"
                    onClick={handleShowVideoModal}
                    disabled={getTotalVideoCount() === 0}
                    className="d-flex align-items-center gap-1"
                >
                    <CameraVideoFill size={16} />
                    Download videos ({getTotalVideoCount()})
                </Button>
                <Button
                    variant="secondary"
                    onClick={handleShowL2rModal}
                    disabled={records.length === 0}
                    className="d-flex align-items-center gap-1"
                >
                    <FileEarmarkCode size={16} />
                    Download all l2r_results
                </Button>
                <Button
                    variant="secondary"
                    onClick={handleShowExtracteddModal}
                    disabled={records.length === 0}
                    className="d-flex align-items-center gap-1"
                >
                    <Images size={16} />
                    Download all extracted
                </Button>
                <Button
                    variant="secondary"
                    onClick={handleShowProcessedModal}
                    disabled={records.length === 0}
                    className="d-flex align-items-center gap-1"
                >
                    <Images size={16} />
                    Download all processed
                </Button>
            </div>
            {/* Modal for downloading videos */}
            <Modal show={showVideoModal} onHide={handleCloseVideoModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Download Videos</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isGeneratingUrls ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-3">Generating download URLs...</p>
                        </div>
                    ) : (
                        <div>
                            {videoUrls.length > 0 ? (
                                <div>
                                    <p>Ready to download {videoUrls.length} videos from {records.length} records. Total size: {formatFileSize(totalVideoSize)}</p>
                                    <DownloadComponent
                                        name="All Videos"
                                        urls={videoUrls}
                                    />
                                </div>
                            ) : (
                                <p>No videos found in the selected records.</p>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal for downloading L2R results */}
            <Modal show={showL2rModal} onHide={handleCloseL2rModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Download L2R Results</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isGeneratingUrls ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-3">Generating download URLs...</p>
                        </div>
                    ) : (
                        <div>
                            {l2rUrls.length > 0 ? (
                                <div>
                                    <p>Ready to download {l2rUrls.length} L2R result files from {records.length} records.</p>
                                    <DownloadComponent
                                        name="All L2R Results"
                                        urls={l2rUrls}
                                    />
                                </div>
                            ) : (
                                <p>No L2R result files found in the selected records.</p>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseL2rModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Modal for downloading processed results */}
            <Modal show={showExtracteddModal} onHide={handleCloseExtractedModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Download extracted frames</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isGeneratingUrls ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-3">Generating download URLs...</p>
                        </div>
                    ) : (
                        <div>
                            {extractedUrls.length > 0 ? (
                                <div>
                                    <p>Ready to download {extractedUrls.length} extracted frames from {records.length} records.</p>
                                    <DownloadComponent
                                        name="All extracted frames"
                                        urls={extractedUrls}
                                    />
                                </div>
                            ) : (
                                <p>No extracted frames found in the selected records.</p>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseExtractedModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Modal for downloading processed results */}
            <Modal show={showProcessedModal} onHide={handleCloseProcessedModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Download processed frames</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isGeneratingUrls ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-3">Generating download URLs...</p>
                        </div>
                    ) : (
                        <div>
                            {processedUrls.length > 0 ? (
                                <div>
                                    <p>Ready to download {processedUrls.length} processed frames from {records.length} records.</p>
                                    <DownloadComponent
                                        name="All Processed frames"
                                        urls={processedUrls}
                                    />
                                </div>
                            ) : (
                                <p>No processed frames found in the selected records.</p>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseProcessedModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DownloadForSelectedRecords;
